import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { fmtDate } from "../lib/utils";
import {
  Button, Input, Select, FormField, Dialog, Sheet,
  Spinner, Empty, PageHeader, SearchInput,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  Card, CardHeader, CardTitle, CardContent,
} from "../components/ui";
import { Plus, User } from "lucide-react";
import { toast } from "sonner";

function emptyForm() {
  return { nombre: "", ruc_ci: "", telefono: "", email: "", direccion: "", tipo: "PERSONA" };
}

export default function Clientes() {
  const [list,    setList]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [open,    setOpen]    = useState(false);
  const [detail,  setDetail]  = useState(null);
  const [edit,    setEdit]    = useState(null);
  const [form,    setForm]    = useState(emptyForm());
  const [saving,  setSaving]  = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/clientes", { params: search ? { q: search } : {} });
    setList(r.data);
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function openEdit(c) {
    setEdit(c);
    setForm({ nombre: c.nombre, ruc_ci: c.ruc_ci ?? "", telefono: c.telefono ?? "", email: c.email ?? "", direccion: c.direccion ?? "", tipo: c.tipo });
    setOpen(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, ruc_ci: form.ruc_ci || null, email: form.email || null, direccion: form.direccion || null };
    try {
      if (edit) {
        await api.put(`/clientes/${edit.id}`, body);
        toast.success("Cliente actualizado");
      } else {
        await api.post("/clientes", body);
        toast.success("Cliente creado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error");
    } finally {
      setSaving(false);
    }
  }

  async function loadDetail(c) {
    const r = await api.get(`/clientes/${c.id}`);
    setDetail(r.data);
  }

  if (loading) return <div className="flex items-center justify-center h-full"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="p-4 sm:p-6">
      {/* Lista */}
      <div>
        <PageHeader title="Clientes" sub={`${list.length} registros`}>
          <Button onClick={() => { setEdit(null); setForm(emptyForm()); setOpen(true); }}>
            <Plus className="w-4 h-4" /> Nuevo cliente
          </Button>
        </PageHeader>

        <SearchInput value={search} onChange={setSearch} placeholder="Buscar por nombre o RUC/CI…" className="w-full sm:w-64 mb-4" />

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>RUC/CI</TableHead>
              <TableHead>Teléfono</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 && (
              <TableRow><TableCell colSpan={6}><Empty message="Sin clientes" icon={User} /></TableCell></TableRow>
            )}
            {list.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium text-sm">{c.nombre}</TableCell>
                <TableCell className="font-mono text-xs">{c.ruc_ci ?? "—"}</TableCell>
                <TableCell className="text-sm">{c.telefono ?? "—"}</TableCell>
                <TableCell className="text-sm">{c.email ?? "—"}</TableCell>
                <TableCell className="text-xs">{c.tipo}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <button onClick={() => loadDetail(c)} className="text-xs text-primary hover:underline">Ver</button>
                    <button onClick={() => openEdit(c)} className="text-xs text-muted-foreground hover:underline ml-2">Editar</button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Detalle — Sheet (funciona en móvil y escritorio) */}
      <Sheet open={!!detail} onClose={() => setDetail(null)} title={detail?.nombre ?? "Cliente"}>
        {detail && (
          <div className="space-y-4">
            <Card>
              <CardContent className="text-sm space-y-1 pt-4">
                {detail.ruc_ci && <p><span className="text-muted-foreground">RUC/CI: </span>{detail.ruc_ci}</p>}
                {detail.telefono && <p><span className="text-muted-foreground">Tel: </span>{detail.telefono}</p>}
                {detail.email && <p><span className="text-muted-foreground">Email: </span>{detail.email}</p>}
                {detail.direccion && <p><span className="text-muted-foreground">Dir: </span>{detail.direccion}</p>}
              </CardContent>
            </Card>
            {(detail.vehiculos?.length > 0) && (
              <Card>
                <CardHeader><CardTitle className="text-sm">Vehículos</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {detail.vehiculos.map((v) => (
                    <div key={v.id} className="text-sm border-b border-border pb-2 last:border-0 last:pb-0">
                      <p className="font-medium">{v.marca} {v.modelo}</p>
                      <p className="text-xs text-muted-foreground">{v.patente ?? "sin patente"} · {v.color ?? "—"}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </Sheet>

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} title={edit ? "Editar cliente" : "Nuevo cliente"} size="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Nombre / Razón social *" className="col-span-full">
              <Input value={form.nombre} onChange={(e) => s("nombre", e.target.value)} required />
            </FormField>
            <FormField label="Tipo">
              <Select value={form.tipo} onChange={(e) => s("tipo", e.target.value)}>
                <option value="PERSONA">Persona física</option>
                <option value="EMPRESA">Empresa</option>
              </Select>
            </FormField>
            <FormField label="RUC / CI">
              <Input value={form.ruc_ci} onChange={(e) => s("ruc_ci", e.target.value)} />
            </FormField>
            <FormField label="Teléfono">
              <Input value={form.telefono} onChange={(e) => s("telefono", e.target.value)} />
            </FormField>
            <FormField label="Email">
              <Input type="email" value={form.email} onChange={(e) => s("email", e.target.value)} />
            </FormField>
            <FormField label="Dirección" className="col-span-full">
              <Input value={form.direccion} onChange={(e) => s("direccion", e.target.value)} />
            </FormField>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={saving}>Guardar</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
